import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip
} from '@mui/material';

/**
 * MemberAllocationTable Component
 * 
 * Displays monthly allocation percentages across all milestones for assigned members
 * and unassigned roles. Extracted from StaffingStep for better maintainability.
 */
const MemberAllocationTable = ({
  memberAllocationSummary,
  getAllocationStatus,
  getAllocationColor,
  formatCurrency
}) => {
  if (!memberAllocationSummary || 
      (memberAllocationSummary.assignedMembers.length === 0 && 
       memberAllocationSummary.unassignedRoles.length === 0)) {
    return null;
  }

  return (
    <Card elevation={1} sx={{ mb: 2 }}>
      <CardHeader
        title="Member Allocation Summary"
        subheader="Monthly allocation percentages across all milestones in this outcome"
      />
      <CardContent>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 150 }}>Member/Role</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Jan</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Feb</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Mar</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Apr</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>May</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Jun</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Jul</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Aug</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Sep</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Oct</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Nov</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Dec</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>Status</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Total Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Assigned Members */}
              {memberAllocationSummary.assignedMembers.map((member) => {
                const allocationStatus = getAllocationStatus(member.monthlyAllocations);
                return (
                  <TableRow key={member.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {member.name}
                      </Typography>
                    </TableCell>
                    {member.monthlyAllocations.map((allocation, monthIndex) => (
                      <TableCell key={monthIndex} align="center">
                        <Chip
                          label={`${Math.round(allocation)}%`}
                          size="small"
                          color={getAllocationColor(allocation)}
                          variant={allocation === 0 ? "outlined" : "filled"}
                          sx={{
                            fontSize: '0.65rem',
                            height: '20px',
                            minWidth: '45px',
                            '& .MuiChip-label': {
                              px: 0.5
                            }
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Chip
                        label={allocationStatus}
                        size="small"
                        color={
                          allocationStatus === 'Over Allocated' ? 'error' :
                          allocationStatus === 'Fully Allocated' ? 'success' : 'info'
                        }
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: '18px' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(member.totalCost)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              
              {/* Unassigned Roles */}
                      {memberAllocationSummary.unassignedRoles.map((role) => {
          return (
                  <TableRow key={role.id} sx={{ bgcolor: 'warning.light', opacity: 0.7 }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontStyle: 'italic' }}>
                        {role.name}
                      </Typography>
                    </TableCell>
                    {role.monthlyAllocations.map((allocation, monthIndex) => (
                      <TableCell key={monthIndex} align="center">
                        <Chip
                          label={`${Math.round(allocation)}%`}
                          size="small"
                          color={getAllocationColor(allocation)}
                          variant={allocation === 0 ? "outlined" : "filled"}
                          sx={{
                            fontSize: '0.65rem',
                            height: '20px',
                            minWidth: '45px',
                            '& .MuiChip-label': {
                              px: 0.5
                            }
                          }}
                        />
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Chip
                        label="Unassigned"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: '18px' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(role.totalCost)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default MemberAllocationTable;